import React, { FunctionComponent } from "react";

import { KeyboardNavigationContextProvider } from "@multiweb/accessibility";
import { bemClassNames } from "@multiweb/reactUtils";
import { FlagColor } from "@multiweb/types/react";
import { Key } from "Global/Base/Utils/EventUtils";
import { FlagCircleWithTitle } from "../FlagCircleWithTitle";

export type FlagGroupProps = {
    /** Flagy ke zobrazení */
    flags: IFlagGroupColor[];
    /** Zda zaměřit první prvek */
    focusFirstItem?: boolean;
    /** Callback volaný při změně stavu flagu */
    onChange: (color: FlagColor, checked: boolean) => void;
};

const classNames = bemClassNames("flag-group-component");

const SORTED_COLORS: FlagColor[] = [
    "red",
    "deeporange",
    "orange",
    "amber",
    "yellow",
    "lime",
    "lightgreen",
    "green",
    "teal",
    "aqua",
    "lightblue",
    "blue",
    "navy",
    "deeppurple",
    "fuchsia",
    "pink",
    "gray",
    "silver",
    "black",
    "brown",
    "none",
];

const NEUTRAL_COLORS: FlagColor[] = [
    "none", "brown", "silver", "gray", "black",
];

/** Zobrazí skupinu flagů */
export const FlagGroup: FunctionComponent<FlagGroupProps> = ({ flags, focusFirstItem = false, onChange }) => {
    const sortedFlags = flags.sort(compare);

    function compare(a: IFlagGroupColor, b: IFlagGroupColor): number {
        const indexA = SORTED_COLORS.indexOf(a.color);
        const indexB = SORTED_COLORS.indexOf(b.color);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        if (indexA > indexB) return 1;
        if (indexA < indexB) return -1;
        return 0;
    }

    let selectedItemIndex = -1;
    if (focusFirstItem) {
        selectedItemIndex = sortedFlags.findIndex(f => f.isSelected && f.color !== "none");
        if (selectedItemIndex < 0) {
            selectedItemIndex = 0;
        }
    }

    return (
        <div className={classNames()}>
            <KeyboardNavigationContextProvider nextItemKey={Key.ArrowDown} previousItemKey={Key.ArrowUp}>
                <div className={classNames("subgroup")}>
                    {sortedFlags.filter(f => !NEUTRAL_COLORS.includes(f.color)).map((flag, index) => (
                        <FlagCircleWithTitle key={flag.color} {...flag} onChange={onChange} isInitFocused={index === selectedItemIndex} />
                    ))}
                </div>
                <div className={classNames("subgroup")}>
                    {sortedFlags.filter(f => NEUTRAL_COLORS.includes(f.color)).map((flag, index) => (
                        <FlagCircleWithTitle key={flag.color} {...flag} onChange={onChange} isInitFocused={(index + SORTED_COLORS.length - NEUTRAL_COLORS.length) === selectedItemIndex } />
                    ))}
                </div>
            </KeyboardNavigationContextProvider>
        </div>
    );
};

/** Obsahuje definici jednoho flagu */
export interface IFlagGroupColor {
    /** Zobrazená barva */
    color: FlagColor;
    /** Titulek */
    title?: string;
    /** Zda má být zobrazen jako označený */
    isSelected: boolean;
}
